import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import {
  BUTTON_BOLD,
  BUTTON_ITALIC,
  BUTTON_TITLE,
  BUTTON_SUBTITLE,
  BUTTON_NUMBERING,
  BUTTON_BULLETS,
  BUTTON_BLOCKQUOTE,
  BUTTON_CODE_BLOCK,
  BUTTON_LINK,
  BUTTON_HEADER_DROPDOWN,
  BUTTON_LIST_DROPDOWN,
  BUTTON_ALIGN_TEXT_DROPDOWN,
  BUTTON_IMAGE,
  BUTTON_IMAGE_MEDIA_DIALOG,
  BUTTON_VIDEO,
} from './constants/buttons';
import { MODAL_TYPE_TEXT_EDITOR_VIDEO_EMBED } from '../../constants/modal-types';
import { RichUtils } from 'draft-js';
import { connect } from 'react-redux';
import { cancelUploads } from '../../actions/cancel-uploads';
import { insertTextEditorEntity } from '../../actions/insert-text-editor-entity';
import { openModal } from '../../actions/open-modal';
import { setTextEditorEntityData } from '../../actions/set-text-editor-entity-data';
import { updateTextEditor } from '../../actions/update-text-editor';
import { upload, TYPE_IMAGE } from '../../actions/upload';
import { uploadEnded } from '../../actions/upload-ended';
import { uploadStarted } from '../../actions/upload-started';
import { getFocusableContainerId, hasLinksInSelection } from './modifiers/text-editor-utils';
import * as blockModifiers from './modifiers/block-type-modifiers';
import * as linkModifiers from './modifiers/link-modifiers';
import * as textAlignmentModifiers from './modifiers/text-alignment-modifiers';
import buttons from './buttons';
import { getTextEditorStateByKey } from '../../selectors/text-editor-selectors';
import { isEditor } from '../../selectors/view-mode-selectors';
import { handleEnterKeyUp } from '../../services/accessibility';
import resizeImage from '../../services/resize-image';

class TextEditorButtonGroup extends Component {
  hasInlineStyle = inlineStyle => this.props.editorState.getCurrentInlineStyle().has(inlineStyle);
  setInlineStyle = inlineStyle => this.setEditorState(RichUtils.toggleInlineStyle(this.props.editorState, inlineStyle));

  hasBlockType = blockType => blockModifiers.hasBlockType(blockType, this.props.editorState);
  toggleBlockType = blockType => this.setEditorState(blockModifiers.toggleBlockType(blockType, this.props.editorState));
  toggleBlockStyleAndEnsureSpaces = blockType =>
    this.setEditorState(blockModifiers.toggleBlockTypeAndEnsureSpaces(blockType, this.props.editorState));

  handleLinkButtonClick = () => {
    const { editorState, identifier, openModal } = this.props;
    const newState = linkModifiers.toggleLink({ editorState, identifier, openModal });
    if (newState) {
      this.setEditorState(newState);
    }
  };

  isLinkButtonActive = () => hasLinksInSelection(this.props.editorState);

  hasTextAlignment = textAlignment => {
    const { editorState } = this.props;
    const currentTextAlignment = editorState
      .getCurrentContent()
      .getBlockForKey(editorState.getSelection().getStartKey())
      .getData()
      .get('textAlignment');

    return currentTextAlignment === textAlignment && currentTextAlignment !== 'left';
  };

  setTextAlignment = textAlignment => {
    const newEditorState = textAlignmentModifiers.setTextAlignment(textAlignment, this.props.editorState);
    this.setEditorState(newEditorState);
  };

  handleFileSelect = imageData => {
    const { insertTextEditorEntity, editorState, identifier } = this.props;
    const props = { src: imageData };
    const entityKey = editorState
      .getCurrentContent()
      .createEntity('IMAGE', 'IMMUTABLE', props)
      .getLastCreatedEntityKey();

    insertTextEditorEntity({
      entityKey,
      identifier,
    });
  };

  handleUploadImage = event => {
    const file = event.target.files[0];
    this.props.uploadStarted({ type: TYPE_IMAGE });
    return resizeImage(file).then(imageData => {
      const { insertTextEditorEntity, editorState, upload, identifier, uploadEnded } = this.props;
      const props = {
        src: imageData,
        isLoading: true,
      };
      const entityKey = editorState
        .getCurrentContent()
        .createEntity('IMAGE', 'IMMUTABLE', props)
        .getLastCreatedEntityKey();

      insertTextEditorEntity({
        entityKey,
        identifier,
      });

      const shouldUploadDataUrl = file.type !== 'image/gif';
      upload(shouldUploadDataUrl ? imageData.dataUrl : file, TYPE_IMAGE, file => {
        uploadEnded({ type: TYPE_IMAGE });
        return setTextEditorEntityData({
          entityData: {
            src: file,
            isLoading: false,
          },
          entityKey,
          identifier,
        });
      });
    });
  };

  handleVideoEmbed = () => {
    const { identifier } = this.props;
    this.props.openModal(MODAL_TYPE_TEXT_EDITOR_VIDEO_EMBED, { identifier }, getFocusableContainerId(identifier));
  };

  setEditorState = editorState =>
    this.props.updateTextEditor({
      identifier: this.props.identifier,
      editorState,
    });

  getButtonProps = type => {
    switch (type) {
      case BUTTON_BOLD:
      case BUTTON_ITALIC:
        return {
          hasInlineStyle: this.hasInlineStyle,
          setInlineStyle: this.setInlineStyle,
        };
      case BUTTON_TITLE:
      case BUTTON_SUBTITLE:
      case BUTTON_NUMBERING:
      case BUTTON_BULLETS:
      case BUTTON_BLOCKQUOTE:
        return {
          isBlockType: this.hasBlockType,
          toggleBlockType: this.toggleBlockType,
        };

      case BUTTON_CODE_BLOCK:
        return {
          isBlockType: this.hasBlockType,
          toggleBlockType: this.toggleBlockStyleAndEnsureSpaces,
        };

      case BUTTON_HEADER_DROPDOWN:
      case BUTTON_LIST_DROPDOWN:
        return {
          isActive: this.hasBlockType,
          onClick: this.toggleBlockType,
        };

      case BUTTON_ALIGN_TEXT_DROPDOWN:
        return {
          isActive: this.hasTextAlignment,
          onClick: this.setTextAlignment,
        };

      case BUTTON_LINK:
        return {
          isActive: this.isLinkButtonActive(),
          onClick: this.handleLinkButtonClick,
        };

      case BUTTON_IMAGE:
        return {
          onFileChange: this.handleUploadImage,
        };

      case BUTTON_IMAGE_MEDIA_DIALOG:
        return {
          onFileSelect: this.handleFileSelect,
        };

      case BUTTON_VIDEO:
        return {
          onClick: this.handleVideoEmbed,
          onKeyUp: handleEnterKeyUp(this.handleVideoEmbed),
        };

      default:
        return {};
    }
  };

  renderButton = (type, key) => {
    const { isInverted, disableOnBlur, editorState, isEditor, theme } = this.props;

    if (isEditor && type === BUTTON_IMAGE) {
      type = BUTTON_IMAGE_MEDIA_DIALOG;
    }

    const Component = buttons[type];
    const props = {
      ...this.getButtonProps(type),
      isInverted,
      isDisabled: disableOnBlur && !editorState.getSelection().getHasFocus(),
    };
    return (
      <div className={this.props.buttonClassName} key={key}>
        <Component {...props} theme={theme}/>
      </div>
    );
  };

  render() {
    const { containerClassName, buttons } = this.props;
    return (
      <div className={containerClassName}>
        {_.map(buttons, (button, index) => this.renderButton(button, index))}
      </div>
    );
  }
}

TextEditorButtonGroup.propTypes = {
  editorState: PropTypes.object,
  buttons: PropTypes.arrayOf(PropTypes.oneOf(Object.keys(buttons))),
  containerClassName: PropTypes.string,
  buttonClassName: PropTypes.string,
  identifier: PropTypes.string,
  isInverted: PropTypes.bool,
  openModal: PropTypes.func,
  upload: PropTypes.func,
  cancelUploads: PropTypes.func,
  uploadStarted: PropTypes.func,
  uploadEnded: PropTypes.func,
  updateTextEditor: PropTypes.func,
  setTextEditorEntityData: PropTypes.func,
  insertTextEditorEntity: PropTypes.func,
  disableOnBlur: PropTypes.bool,
  isEditor: PropTypes.bool,
  theme: PropTypes.object,
};

TextEditorButtonGroup.defaultProps = {
  buttons: [],
};

const mapStateToProps = (state, ownProps) => ({
  editorState: getTextEditorStateByKey(state, ownProps.identifier),
  isEditor: isEditor(state),
});

const mapDispatchToProps = {
  openModal,
  upload,
  cancelUploads,
  uploadStarted,
  uploadEnded,
  updateTextEditor,
  setTextEditorEntityData,
  insertTextEditorEntity,
};

export default connect(mapStateToProps, mapDispatchToProps)(TextEditorButtonGroup);



// WEBPACK FOOTER //
// ./app/components/text-editor/text-editor-button-group.js